import React, { useState, useEffect, useRef } from 'react'
import { CalendarIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import ButtonEl from '../helpers/ctas/Button'
import { formatDate } from '../../helpers/util'
import CalendarPicker from '../helpers/search/filters/CalendarPicker'
import { dataLayerPush } from '../../helpers/thirdparty'

// Formats an ISO date string (YYYY-MM-DD) as DD/M/YYYY for the data layer
const formatFilterDate = (iso) => {
  if (!iso) return null
  const [y, m, d] = iso.split('-')
  return `${parseInt(d)}/${parseInt(m)}/${y}`
}

// --- ICONS ---
const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-3 h-3 text-white"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
)

// --- ACCORDION COMPONENT ---
const Accordion = ({ title, defaultExpanded = false, children }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  // The grid-rows trick clips content with overflow-hidden so it can animate to an
  // arbitrary height. But overflow-hidden also clips absolutely-positioned popups
  // inside the panel (e.g. the Date Range calendar). So we only keep it hidden while
  // the height is animating, then switch back to visible once fully open. `animating`
  // starts false so a default-expanded panel is immediately overflow-visible on load.
  const [animating, setAnimating] = useState(false)
  const contentId = `accordion-content-${title.replace(/\s+/g, '-').toLowerCase()}`

  const handleToggle = () => {
    setAnimating(true)
    setIsExpanded((prev) => !prev)
  }

  const showOverflow = isExpanded && !animating

  return (
    <div className="border-b border-(--color-border-secondary)">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={handleToggle}
        // Removed rounded-sm and px-1, added ring-inset so focus rings don't get cut off
        className="flex items-center justify-between w-full py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-inset transition-colors hover:text-(--color-primary-text)"
      >
        <span className="font-medium text-(--sb-common-base-body-text-color) text-lg">{title}</span>
        <ChevronDownIcon
          className={`${isExpanded ? 'rotate-180' : ''} h-4 w-4 text-(--sb-search-filter-chevron-down-icon-color) transition-transform duration-200`}
          aria-hidden="true"
        />
      </button>
      <div
        id={contentId}
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        onTransitionEnd={(e) => {
          // Ignore bubbled transitions from nested grids (e.g. the FacetSection
          // "Show all" animation) — only react to this panel's own height change.
          if (e.target === e.currentTarget && e.propertyName === 'grid-template-rows') setAnimating(false)
        }}
      >
        <div
          className={`transition-opacity duration-300 ease-in-out ${showOverflow ? 'overflow-visible' : 'overflow-hidden'} ${isExpanded ? 'opacity-100 visible pb-2' : 'opacity-0 invisible'}`}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

// --- FACET SECTION ---
const FacetSection = ({ pluralName, options = [], selectedValues = [], onChange }) => {
  const [showAll, setShowAll] = useState(false)
  const handleToggleOption = (value) => {
    selectedValues.includes(value)
      ? onChange(selectedValues.filter((v) => v !== value))
      : onChange([...selectedValues, value])
  }

  // The first 5 options are always rendered; the rest live inside their own grid-rows
  // container so "Show all / Show less" animates to the natural height of the extra
  // options instead of popping in/out. (No popups here, so overflow-hidden is permanent.)
  const baseOptions = options.slice(0, 5)
  const extraOptions = options.slice(5)

  const renderOption = (option) => (
    <label
      key={option.value}
      // Changed to py-3, border-t, and ring-inset. Removed px-1 and rounded-md.
      className="flex items-start gap-3 cursor-pointer group py-3 border-t border-(--color-border-secondary) focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-inset"
    >
      <div className="relative flex items-center justify-center w-5 h-5 mt-[2px] shrink-0 ml-4">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={selectedValues.includes(option.value)}
          onChange={() => handleToggleOption(option.value)}
        />
        <div className="w-5 h-5 border border-(--color-border-tertiary) rounded-md peer-checked:bg-(--sb-checkbox-selected-color) peer-checked:border-(--sb-checkbox-selected-color) transition-colors"></div>
        {selectedValues.includes(option.value) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CheckIcon />
          </div>
        )}
      </div>
      <span className="text-lg text-(--sb-common-base-body-text-color) leading-snug group-hover:text-(--color-primary-text)">
        {option.value} {option.count && <span>({option.count})</span>}
      </span>
    </label>
  )

  return (
    // Removed gap-3 so borders touch each other directly
    <div className="flex flex-col w-full">
      {/* "All" Option */}
      <label className="flex items-start gap-3 cursor-pointer group py-3 border-t border-(--color-border-secondary) focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-inset">
        <div className="relative flex items-center justify-center w-5 h-5 mt-[2px] shrink-0 ml-4">
          <input
            type="checkbox"
            className="peer sr-only"
            checked={selectedValues.length === 0}
            onChange={() => onChange([])}
          />
          <div className="w-5 h-5 border border-(--color-border-tertiary) rounded-md peer-checked:bg-(--sb-checkbox-selected-color) peer-checked:border-(--sb-checkbox-selected-color) transition-colors"></div>
          {selectedValues.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <CheckIcon />
            </div>
          )}
        </div>
        <span className="text-lg text-(--sb-common-base-body-text-color) font-normal group-hover:text-(--color-primary-text)">
          All {pluralName}
        </span>
      </label>

      {/* First 5 options (always visible) */}
      {baseOptions.map(renderOption)}

      {/* Remaining options, height-animated by the Show all / Show less toggle */}
      {extraOptions.length > 0 && (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${showAll ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        >
          <div className="overflow-hidden">{extraOptions.map(renderOption)}</div>
        </div>
      )}

      {/* Show All Toggle */}
      {options.length > 5 && (
        <div className="py-3 border-t border-(--color-border-secondary)">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            aria-expanded={showAll}
            // ml-12 (3rem) aligns the link with where the checkbox label text starts:
            // checkbox ml-4 (1rem) + box w-5 (1.25rem) + label gap-3 (0.75rem). (NDS-149)
            className="ml-12 text-left text-lg font-normal text-(--sb-common-base-body-text-color) underline   hover:text-(--color-primary-text) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-inset rounded-sm"
          >
            {showAll ? 'Show less' : 'Show all'}
          </button>
        </div>
      )}
    </div>
  )
}

// --- DATE RANGE SECTION ---
const DateRangeSection = ({ dateFrom, dateTo, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  const displayLabel = !dateFrom && !dateTo ? 'All dates' : `${formatDate(dateFrom)} - ${formatDate(dateTo)}`

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    // Added border-t and py-3 here to perfectly match the Checkbox rows
    <div className="relative py-3 border-t border-(--color-border-secondary)" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        // Removed rounded, added ring-inset
        className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-inset w-full text-left"
      >
        <CalendarIcon className="w-6 h-6 text-(--color-primary-text) shrink-0" aria-hidden="true" />
        <span className="text-lg text-(--sb-common-base-body-text-color) underline   decoration-1 group-hover:text-(--color-primary-text)">
          {displayLabel}
        </span>
      </button>

      {isOpen && (
        <CalendarPicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onApply={(from, to) => {
            onChange('dateFrom', from)
            onChange('dateTo', to)
            setIsOpen(false)
          }}
          onCancel={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

// --- MAIN SEARCH FILTERS ---
const SearchFilters = ({ facets = {}, appliedFilters = {}, onApplyFilters, onClearFilters, category }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState(appliedFilters)

  useEffect(() => {
    setLocalFilters(appliedFilters)
  }, [appliedFilters])

  const handleFacetChange = (key, val) => setLocalFilters((prev) => ({ ...prev, [key]: val }))
  const handleApply = () => {
    onApplyFilters(localFilters)
    setIsMobileMenuOpen(false)

    const from = formatFilterDate(localFilters.dateFrom)
    const to = formatFilterDate(localFilters.dateTo)
    dataLayerPush({
      event: 'filter_applied',
      category: category || undefined,
      topic: localFilters.topic?.length ? localFilters.topic.join(', ') : 'All topics',
      audience: localFilters.audience?.length ? localFilters.audience.join(', ') : 'All audiences',
      location: localFilters.location?.length ? localFilters.location.join(', ') : 'All locations',
      date_range: from && to ? `${from} - ${to}` : 'All dates',
    })
  }
  const handleClear = () => {
    setLocalFilters({})
    onClearFilters()
    setIsMobileMenuOpen(false)
  }

  const renderFilterContent = (isMobile) => (
    <div className="flex flex-col h-full bg-white px-4 lg:px-0">
      {isMobile && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200 mb-4">
          <h2 className="text-xl font-light text-(--color-primary-text)">Filter by</h2>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 focus-visible:ring-2 focus-visible:ring-blue-600 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      )}
      {!isMobile && <h2 className="text-4xl font-light text-(--color-primary-text) mb-6 tracking-wide">Filter by</h2>}

      <div className="flex-1 overflow-y-auto lg:overflow-y-visible pb-24 lg:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Accordion title="Topic" defaultExpanded={true}>
          <FacetSection
            pluralName="topics"
            options={facets.topic || []}
            selectedValues={localFilters.topic || []}
            onChange={(v) => handleFacetChange('topic', v)}
          />
        </Accordion>
        <Accordion title="Audience" defaultExpanded={true}>
          <FacetSection
            pluralName="audiences"
            options={facets.audience || []}
            selectedValues={localFilters.audience || []}
            onChange={(v) => handleFacetChange('audience', v)}
          />
        </Accordion>
        <Accordion title="Location" defaultExpanded={true}>
          <FacetSection
            pluralName="locations"
            options={facets.location || []}
            selectedValues={localFilters.location || []}
            onChange={(v) => handleFacetChange('location', v)}
          />
        </Accordion>
        <Accordion title="Date Range" defaultExpanded={true}>
          <DateRangeSection
            dateFrom={localFilters.dateFrom}
            dateTo={localFilters.dateTo}
            onChange={(k, v) => handleFacetChange(k, v)}
          />
        </Accordion>
      </div>

      <div className="sticky bottom-0 left-0 w-full p-4 lg:py-6 border-t border-(--color-border-secondary) lg:border-t-0 bg-white z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 w-full">
          <ButtonEl theme="primary" onClick={handleApply} className="w-full uppercase text-sm! font-medium px-2!">
            Apply Filters
          </ButtonEl>
          <ButtonEl
            theme="secondary"
            onClick={handleClear}
            className="w-full uppercase text-sm! font-medium px-2! bg-(--color-button-secondary-hover-bg)! text-(--color-button-secondary-hover-text)! border-(--color-button-secondary-hover-border)!"
          >
            Clear Filters
          </ButtonEl>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile Trigger Button */}
      <div className="lg:hidden w-full mb-6">
        <ButtonEl
          theme="primary"
          onClick={() => setIsMobileMenuOpen(true)}
          className="w-full rounded-full! uppercase text-sm! font-medium px-2!"
        >
          Filter By
        </ButtonEl>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-full">{renderFilterContent(false)}</div>

      {/* SMOOTH MOBILE MODAL */}
      <div
        role="dialog"
        aria-modal={isMobileMenuOpen}
        // Only while open, so the section isn't left elevated by an off-screen panel (see .inner in App.css)
        data-overlay={isMobileMenuOpen ? 'modal' : undefined}
        // Using ease-out for a snappy entrance and smooth deceleration
        className={`fixed inset-0 z-[100] lg:hidden flex flex-col bg-white transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-y-0 shadow-2xl' : 'translate-y-full'
        }`}
      >
        {renderFilterContent(true)}
      </div>
    </>
  )
}

export default SearchFilters
